import { Box } from "@mui/material";
import { useState, useEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { NavigationBreadcrumbs, Spinner } from "../../components";
import { LEVEL, NAVBAR_ITEMS } from "../../consts";
import { useStartOrResumeQuery } from "../../queries";
import { useGetEditorialQuery } from "../../queries/editorial";
import { useAuthStore, useLanguageStore, useNavbarStore } from "../../stores";
import { getError } from "../../utils/error";
import { getEnvironmentVariables, getPreviousRoute } from "../../utils/general";
import { getScormTrackerInitText } from "../../utils/scormTracker";

const { scormJs } = getEnvironmentVariables();

// const html = `<!DOCTYPE html>
// <html lang="en">
//   <head>
//     <meta charset="UTF-8" />
//     <meta name="viewport" content="width=device-width, initial-scale=1.0" />
//     <link rel="preconnect" href="https://fonts.googleapis.com">
//     <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
//     <link href="https://fonts.googleapis.com/css2?family=Inter:ital,opsz,wght@0,14..32,100..900;1,14..32,100..900&display=swap" rel="stylesheet">
//     <title>Document</title>
//   </head>
//     <body><main style="font-family:Inter;background-color:#1B1B1B;color:#FFFFFF;padding:50px 25px"><div><div style="display:flex;flex-direction:column;justify-content:center;align-items:center;padding:30px"><h1 style="margin-top:10px">Lorem ipsum</h1><span style="margin-top:15px">lorem ipsum description</span><div style="display:flex;flex-direction:row;gap:20px;margin-top:20px"><div style="border:2px solid #696969;display:inline-flex;flex-direction:column;gap:10px;padding:16px 16px;border-radius:16px;justify-content:center;align-items:center;width:280px;height:280px;text-align:center;background-color:#1B1B1B"><svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#72CC52" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-book" aria-hidden="true"><path d="M4 19.5v-15A2.5 2.5 0 0 1 6.5 2H19a1 1 0 0 1 1 1v18a1 1 0 0 1-1 1H6.5a1 1 0 0 1 0-5H20"></path></svg><span style="margin-top:10px;font-size:16px">Title Card</span><span style="font-size:16px">description</span></div><div style="display:flex;align-items:center"><svg xmlns="http://www.w3.org/2000/svg" width="72" height="72" viewBox="0 0 24 24" fill="none" stroke="#72CC52" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-arrow-right" aria-hidden="true"><path d="M5 12h14"></path><path d="m12 5 7 7-7 7"></path></svg></div><div style="border:2px solid #696969;display:inline-flex;flex-direction:column;gap:10px;padding:16px 16px;border-radius:16px;justify-content:center;align-items:center;width:280px;height:280px;text-align:center;background-color:#1B1B1B"><svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#72CC52" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-building" aria-hidden="true"><path d="M12 10h.01"></path><path d="M12 14h.01"></path><path d="M12 6h.01"></path><path d="M16 10h.01"></path><path d="M16 14h.01"></path><path d="M16 6h.01"></path><path d="M8 10h.01"></path><path d="M8 14h.01"></path><path d="M8 6h.01"></path><path d="M9 22v-3a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v3"></path><rect x="4" y="2" width="16" height="20" rx="2"></rect></svg><span style="margin-top:10px;font-size:16px">Title Card</span><span style="font-size:16px">description</span></div></div></div><div style="padding:30px;display:flex;flex-direction:row;gap:30px;height:420px"><div style="flex:1;display:flex;flex-direction:column;justify-content:center;gap:15px"><h1>Lorem ipsum</h1><span>Lorem ipsum dolor sit, amet consectetur adipisicing elit. Magnam, numquam! Nisi deserunt porro quia facilis qui delectus molestiae perspiciatis mollitia quisquam odio laudantium eum iusto, assumenda maiores dolore vero unde?</span></div><div style="flex:1.2;display:flex;align-items:center"><iframe src="https://www.youtube.com/embed/bLvqoHBptjg?si=2NDFwwX5tgqx5eFx" title="Video player" width="600px" height="350px" style="border-radius:15px" referrerpolicy="strict-origin-when-cross-origin"></iframe></div></div></div></main></body>
//     <script src="https://unpkg.com/lucide@latest"></script>
//     <script>
//       lucide.createIcons();
//     </script>
// </html>`;

const isNotFoundCode = "404";

export function Editorial() {

  const { t } = useTranslation();
  const { idEditorial } = useParams();
  const navigate = useNavigate();
  const [showModal, setShowModal] = useState(false);

  const {
    data: editorialObject,
    error:editorialObjectError,
    isLoading: editorialObjectIsFetching
  } = useGetEditorialQuery({ idEditorial: Number(idEditorial), language: "it" });

  const { idActivity } = useParams<{
      idActivity: string
    }>();

  const idNumber = idActivity ? Number(idActivity) : NaN;
  const language = useLanguageStore(state => state.language)?.id;
  const setSpinnerLoginVisible = useAuthStore(state => state.setSpinnerLoginVisible);
  const accessToken = useAuthStore(state => state.session?.getAccessToken().getJwtToken()) ?? "";
  const refreshToken = useAuthStore(state => state.session?.getRefreshToken().getToken()) ?? "";
  const sessionToken = useAuthStore(state => state.sessionToken);
  const userData = useAuthStore(state => state.userData);
  const navbarItem = useNavbarStore(state => state.item);
  const trackScriptInjected = useRef(false);

  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const parentId = searchParams.get("parentId") ?? "";
  const grandParentId = searchParams.get("grandParentId") ?? "";

  const {
    data: learningObject,
    error:learningObjectError,
    isLoading: learningObjectIsFetching
  } = useStartOrResumeQuery({
    grandParentId,
    language,
    learningObjectId: idNumber,
    learningObjectTypology: LEVEL.DA_URL_RES,
    parentId
  });
  
  const hasWrongType = useMemo(() => {
    if(learningObjectIsFetching || !learningObject?.learningObjectTypology) return false;
    return learningObject.learningObjectTypology !== LEVEL.DA_URL_RES;
  }, [learningObjectIsFetching, learningObject]);
  
  const editorialNotFound = getError(editorialObjectError)?.[0]?.cause === isNotFoundCode;
  const isNotFound = getError(learningObjectError)?.[0]?.cause === isNotFoundCode;
  const isNotEnrolled = getError(learningObjectError)?.[0]?.cause === "STUDENT_NOT_ENROLLED";
  
  if(editorialNotFound || isNotFound || hasWrongType){
    navigate("/not-found");
  }
  
  if (isNotEnrolled){
    navigate("/not-found", { state: { "message": t("student.not.enrolled") } });
  }
  
  useEffect(() => {
    if(learningObjectError && !isNotFound && !showModal) setShowModal(true);
  }, [learningObjectError, isNotFound, showModal]);

  useEffect(() => {
    if (
      !learningObject?.trackOnClick
      || trackScriptInjected.current
      || !scormJs
      || !accessToken
      || !refreshToken
      || !sessionToken
      || !userData
    ) return;

    const link = learningObject.link;
    const linkIsValid = !!link && link !== "null" && !/\/null\//.test(link);
    if (!linkIsValid) return;

    const { courseDetails, rootDetails } = learningObject;
    let rootCalculatedId: number | null =
      rootDetails?.learningObjectTypology === LEVEL.PATH ? rootDetails.id : null;
    let courseCalculatedId: number | null = null;
    if (rootDetails?.learningObjectTypology === LEVEL.COURSE && rootDetails.isStandAlone) {
      courseCalculatedId = rootDetails.id;
      rootCalculatedId = null;
    }
    const trackCourseId = courseCalculatedId ?? courseDetails?.id;
    const trackRootId = rootCalculatedId ?? undefined;

    const idPath = trackRootId ?? trackCourseId ?? idNumber;
    const typologyPath = trackRootId
      ? LEVEL.PATH
      : trackCourseId
        ? LEVEL.COURSE
        : LEVEL.DA_URL_RES;
    const detailsPath = `esplora/dettaglio/${idPath}/${typologyPath}`;

    const initText = getScormTrackerInitText({
      accessToken,
      calculatedCourseId: trackCourseId?.toString(),
      calculatedRootId: trackRootId?.toString(),
      detailsPath,
      language,
      learningObject,
      refreshToken,
      sessionToken,
      userData
    });

    const libraryScript = document.createElement("script");
    libraryScript.src = `${scormJs}?hash=${sessionToken}`;
    libraryScript.type = "text/javascript";
    libraryScript.setAttribute("data-react-helmet", "true");
    libraryScript.onload = () => {
      const libraryInit = document.createElement("script");
      libraryInit.type = "text/javascript";
      libraryInit.text = initText;
      document.body.appendChild(libraryInit);
    };
    document.body.appendChild(libraryScript);
    trackScriptInjected.current = true;
  }, [
    accessToken,
    idNumber,
    language,
    learningObject,
    refreshToken,
    sessionToken,
    userData
  ]);

  if(learningObjectIsFetching) return (
    <Box sx={ {
      paddingTop: "32px",
      paddingBottom: "32px"
    } }>
      <Spinner />
    </Box>
  );

  if(!editorialObjectIsFetching) {
    setSpinnerLoginVisible(false);
  }

  const sectionPath = navbarItem === NAVBAR_ITEMS.FOR_YOU ? "per-te" : "esplora";
  const previousPath = getPreviousRoute({
    courseId: parentId ? Number(parentId) : undefined,
    id: idNumber,
    learningObjectTypology: learningObject?.learningObjectTypology ?? LEVEL.DA_URL_RES,
    path: `/${sectionPath}`,
    rootId: grandParentId ? Number(grandParentId) : undefined,
    section: navbarItem
  });

  return editorialObject ? (
    <Box
      sx={ {
        marginBottom: "22px",
        "& nav": {
          paddingLeft: 0
        }
      } }
    >
      <NavigationBreadcrumbs
        learningObjectTypology={ learningObject?.learningObjectTypology ?? LEVEL.DA_URL_RES }
        previousPath={ previousPath }
      />
      <div dangerouslySetInnerHTML={ { __html: editorialObject.html } }></div>
    </Box>
  ) : <div></div>;
}
